import React, { Suspense, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stars } from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as THREE from 'three';
import USMap from './USMap';
import NodeMesh from './NodeMesh';
import EdgeMesh from './EdgeMesh';
import BackgroundGrid from './BackgroundGrid';
import CameraController from './CameraController';
import useStore from '../store';
import { getOptimalCameraPosition } from '../utils/coordinates';

function Scene() {
  const canvasRef = useRef();
  const controlsRef = useRef();
  const { graphData } = useStore();
  
  useEffect(() => {
    if (canvasRef.current && controlsRef.current) {
      const canvas = canvasRef.current.querySelector('canvas');
      if (canvas) {
        canvas.__orbitControls = controlsRef.current;
      }
    }
  }, []);
  
  if (!graphData) return null;
  
  const cameraConfig = getOptimalCameraPosition();
  
  return (
    <Canvas
      ref={canvasRef}
      camera={{
        position: cameraConfig.position,
        fov: cameraConfig.fov,
        near: 0.1,
        far: 1000
      }}
      gl={{
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance'
      }}
      style={{ background: '#0b0b13' }}
    >
      <Suspense fallback={null}>
        <ambientLight intensity={0.4} />
        <pointLight position={[10, 10, 10]} intensity={0.6} color="#05f3ff" />
        <pointLight position={[-10, 5, -10]} intensity={0.4} color="#3bff9f" />
        <directionalLight position={[0, 10, 5]} intensity={0.3} color="#ffffff" />
        
        <Stars radius={100} depth={50} count={800} factor={2} saturation={0} fade speed={0.3} />
        <BackgroundGrid />
        
        <USMap />
        <EdgeMesh edges={graphData.edges} nodes={graphData.nodes} />
        <NodeMesh nodes={graphData.nodes} />
        
        <CameraController target={cameraConfig.target} />
        
        {/* EXTREME ZOOM - minDistance 0.1 (10x closer) */}
        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.05}
          rotateSpeed={0.5}
          zoomSpeed={1.5}
          panSpeed={1.0}
          enablePan={true}
          minDistance={0.1}      // EXTREME CLOSE-UP
          maxDistance={60}
          maxPolarAngle={Math.PI / 2.1}
          target={[0, 0, 0]}
          zoomToCursor={true}
          mouseButtons={{
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.PAN
          }}
          touches={{
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN
          }}
        />
        
        <EffectComposer>
          <Bloom intensity={0.04} luminanceThreshold={0.95} luminanceSmoothing={0.9} radius={0.15} />
        </EffectComposer>
      </Suspense>
    </Canvas>
  );
}

export default Scene;